/**
 * Small pure helpers shared by the Alerts page's table and form: the label and
 * severity a saved problem shows, and the send time in the words the page and
 * the email both use ("7:00 AM Pacific").
 */
import { AuditAlertProblem, AuditAlertProblemOption } from "../../../api/auditAlerts"

export function labelFor(
  problem: AuditAlertProblem,
  options: AuditAlertProblemOption[],
): string {
  return options.find((option) => option.slug === problem.slug)?.label ?? problem.slug
}

export function severityFor(
  problem: AuditAlertProblem,
  options: AuditAlertProblemOption[],
): string {
  return options.find((option) => option.slug === problem.slug)?.severity ?? "warning"
}

/** 7, 0 → "7:00 AM Pacific"; 13, 30 → "1:30 PM Pacific". */
export function formatSendTime(hour: number, minute: number): string {
  const suffix = hour < 12 ? "AM" : "PM"
  const twelveHour = hour % 12 === 0 ? 12 : hour % 12
  return `${twelveHour}:${String(minute).padStart(2, "0")} ${suffix} Pacific`
}

/** "07:00" for an <input type="time">. */
export function toTimeInput(hour: number, minute: number): string {
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
}

export function fromTimeInput(value: string): { hour: number; minute: number } | null {
  // A cleared <input type="time"> reads "" — that is "no time", not 0:00 or
  // NaN (which serialised as null and came back as an unreadable 422).
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!match) return null
  const hour = Number(match[1])
  const minute = Number(match[2])
  if (hour > 23 || minute > 59) return null
  return { hour, minute }
}

/** Comma- or newline-separated addresses, trimmed, blanks dropped. */
export function parseRecipients(text: string): string[] {
  return text.split(/[,\n]/).map((part) => part.trim()).filter(Boolean)
}
